import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClubTheme } from "@/context/ThemeContext";
import QuickReportButton from "@/components/QuickReportButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Envelope, WhatsappLogo, Bank, TennisBall, CalendarCheck, ChatCircle, Buildings, Clock, CurrencyDollar, PencilSimple, Plus, X as XIcon, InstagramLogo } from "@phosphor-icons/react";
import ClubFeed from "@/components/ClubFeed";
import PadelCourt3D from "@/components/PadelCourt3D";
import ClubStreamingSetup from "@/components/ClubStreamingSetup";
import CourtScoreboardWidget from "@/components/CourtScoreboardWidget";
import CourtHistory from "@/components/CourtHistory";
import TrainingPanel from "@/components/TrainingPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_NAMES = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];

export default function ClubDetail() {
  const { clubId } = useParams();
  useClubTheme(clubId);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [schedule, setSchedule] = useState({ prices: { "1h": 0, "1.5h": 0, "2h": 0 }, time_slots: [], operating_days: [] });
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [editingSched, setEditingSched] = useState(false);
  const [schedForm, setSchedForm] = useState({ prices: {}, time_slots: [], operating_days: [] });
  const [newSlot, setNewSlot] = useState("");
  const [savingSched, setSavingSched] = useState(false);

  const canManage =
    user?.role === "admin" ||
    (user?.role === "directivo" && user?.club_id === clubId);

  useEffect(() => {
    Promise.all([
      api.get(`/clubs/${clubId}`),
      api.get(`/court-rentals?club_id=${clubId}&date=${date}`),
      api.get(`/court-rentals/schedule?club_id=${clubId}`),
    ])
      .then(([c, r, s]) => {
        setClub(c.data);
        setRentals(r.data || []);
        setSchedule(s.data);
      })
      .catch(() => toast.error("Error al cargar club"))
      .finally(() => setLoading(false));
  }, [clubId, date]);

  const openSchedEditor = () => {
    setSchedForm({
      prices: { ...schedule.prices },
      time_slots: [...schedule.time_slots],
      operating_days: [...schedule.operating_days],
    });
    setEditingSched(true);
    setNewSlot("");
  };

  const saveSched = async () => {
    setSavingSched(true);
    try {
      const { data } = await api.put(`/clubs/${clubId}/schedule`, {
        prices: {
          "1h": parseFloat(schedForm.prices["1h"]) || 0,
          "1.5h": parseFloat(schedForm.prices["1.5h"]) || 0,
          "2h": parseFloat(schedForm.prices["2h"]) || 0,
        },
        time_slots: [...schedForm.time_slots].sort(),
        operating_days: schedForm.operating_days,
      });
      setSchedule(data);
      setEditingSched(false);
      toast.success("Horarios y precios actualizados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSavingSched(false);
    }
  };

  const toggleDay = (d) => {
    setSchedForm((p) => ({
      ...p,
      operating_days: p.operating_days.includes(d)
        ? p.operating_days.filter((x) => x !== d)
        : [...p.operating_days, d].sort(),
    }));
  };

  const addSlot = () => {
    const s = newSlot.trim();
    if (!/^\d{2}:\d{2}$/.test(s)) { toast.error("Formato HH:MM"); return; }
    if (schedForm.time_slots.includes(s)) { toast.error("Ya existe"); return; }
    setSchedForm((p) => ({ ...p, time_slots: [...p.time_slots, s].sort() }));
    setNewSlot("");
  };

  const removeSlot = (s) => {
    setSchedForm((p) => ({ ...p, time_slots: p.time_slots.filter((x) => x !== s) }));
  };

  const courtBookings = (courtName) => rentals.filter((r) => r.court_name === courtName && r.status !== "cancelada");
  const reserveCourt = (courtName) => navigate(`/dashboard/canchas?club_id=${clubId}&court=${encodeURIComponent(courtName)}&date=${date}`);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  }
  if (!club) {
    return <div className="card-tactical p-12 text-center"><p className="text-gray-400">Club no encontrado</p></div>;
  }

  const courts = club.court_names && club.court_names.length > 0
    ? club.court_names
    : Array.from({ length: club.courts_count || 0 }, (_, i) => `Cancha ${i + 1}`);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="club-detail-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard/clubes")} className="text-gray-400 hover:text-[#CCFF00] -ml-2" data-testid="back-to-clubs">
        <ArrowLeft size={18} className="mr-2" /> Volver a Clubes
      </Button>

      {/* Header */}
      <div className="flex flex-col md:flex-row items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            {club.logo_path ? (
              <img
                src={`${API_URL}/api/clubs/${clubId}/logo`}
                alt={`${club.name} logo`}
                className="w-20 h-20 object-contain bg-white p-2 border border-white/10"
                data-testid="club-logo"
              />
            ) : (
              <div className="w-20 h-20 flex items-center justify-center bg-[#1A1A1A] border border-white/10 text-gray-500 text-[10px] uppercase tracking-widest" data-testid="club-logo-placeholder">
                Sin logo
              </div>
            )}
            {canManage && (
              <label className="absolute -bottom-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-[#CCFF00] text-black font-bold uppercase text-[9px] tracking-wider px-2 py-0.5 cursor-pointer hover:bg-[#B3E600] whitespace-nowrap" data-testid="club-logo-upload-btn">
                <PencilSimple size={10} weight="bold" />
                {club.logo_path ? "Cambiar" : "Subir logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  data-testid="club-logo-file-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const fd = new FormData();
                      fd.append("file", f);
                      await api.post(`/clubs/${clubId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      toast.success("Logo actualizado");
                      const { data } = await api.get(`/clubs/${clubId}`);
                      setClub(data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Error subiendo logo");
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
              </label>
            )}
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Club</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">{club.name}</h1>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-400">
            {club.address && <span className="flex items-center gap-1"><MapPin size={14} className="text-gray-600" />{club.address}</span>}
            {club.phone && <span className="flex items-center gap-1"><Phone size={14} className="text-gray-600" />{club.phone}</span>}
            {club.email && <span className="flex items-center gap-1"><Envelope size={14} className="text-gray-600" />{club.email}</span>}
            {club.whatsapp && <a href={`https://wa.me/52${club.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#CCFF00]"><WhatsappLogo size={14} className="text-green-500" />{club.whatsapp}</a>}
            {club.clabe_bbva && <span className="flex items-center gap-1 font-['JetBrains_Mono'] text-xs"><Bank size={14} className="text-blue-400" />CLABE: {club.clabe_bbva}</span>}
            {club.instagram_handle && (
              <a href={`https://instagram.com/${club.instagram_handle.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-pink-400" data-testid="header-ig">
                <InstagramLogo size={14} className="text-pink-400" weight="fill" />@{club.instagram_handle.replace(/^@/, "")}
              </a>
            )}
          </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <QuickReportButton section="clubs" params={{ club_id: clubId }} label="Reporte del club" withShare />
          <Link to={`/dashboard/chat?room=club_${clubId}`} className="inline-flex">
            <Button variant="outline" className="border-white/20 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 text-xs uppercase font-bold" data-testid="open-club-chat">
              <ChatCircle size={14} className="mr-1" /> Chat del Club
            </Button>
          </Link>
          <Link to={`/dashboard/clubes/${clubId}/ar`} className="inline-flex items-center border border-blue-500/30 text-blue-300 hover:bg-blue-500/10 text-xs uppercase font-bold h-9 px-3" data-testid="club-ar-btn">
            ⚡ Ver en AR
          </Link>
          {canManage && (
            <Button variant="outline" onClick={() => navigate(`/dashboard/clubes`)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-xs uppercase font-bold" data-testid="manage-club">
              <Buildings size={14} className="mr-1" /> Administrar
            </Button>
          )}
        </div>
      </div>

      {/* Streaming setup */}
      <ClubStreamingSetup clubId={clubId} isAdmin={canManage} />

      {/* Schedule & prices card */}
      <div className="card-tactical p-5" data-testid="schedule-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Operacion</p>
            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white">Horarios y Precios</h3>
          </div>
          {canManage && !editingSched && (
            <Button size="sm" variant="outline" onClick={openSchedEditor} className="border-white/20 text-gray-300 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 text-xs uppercase font-bold" data-testid="edit-schedule-btn">
              <PencilSimple size={12} className="mr-1" /> Editar
            </Button>
          )}
        </div>

        {editingSched ? (
          <div className="space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Precios por duracion (MXN)</p>
              <div className="grid grid-cols-3 gap-3">
                {["1h", "1.5h", "2h"].map((k) => (
                  <div key={k}>
                    <label className="text-[10px] text-gray-400 block mb-1">{k === "1h" ? "1 hora" : k === "1.5h" ? "1.5 horas" : "2 horas"}</label>
                    <Input type="number" min="0" step="0.01"
                      value={schedForm.prices[k] ?? ""}
                      onChange={(e) => setSchedForm((p) => ({ ...p, prices: { ...p.prices, [k]: e.target.value } }))}
                      className="bg-[#0A0A0A] border-white/20 text-white h-10 focus:border-[#CCFF00]"
                      data-testid={`sched-price-${k.replace(".", "-")}`} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Dias de operacion</p>
              <div className="flex gap-2 flex-wrap">
                {DAY_NAMES.map((name, idx) => {
                  const active = schedForm.operating_days.includes(idx);
                  return (
                    <button key={idx} type="button" onClick={() => toggleDay(idx)}
                      className={`px-3 h-9 text-xs font-semibold uppercase border ${active ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`}
                      data-testid={`sched-day-${idx}`}>
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Horarios disponibles</p>
              <div className="flex gap-2 mb-2">
                <Input type="time" value={newSlot} onChange={(e) => setNewSlot(e.target.value)}
                  className="bg-[#0A0A0A] border-white/20 text-white h-10 focus:border-[#CCFF00] max-w-[140px]"
                  data-testid="sched-new-slot" />
                <Button type="button" size="sm" onClick={addSlot} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="sched-add-slot">
                  <Plus size={12} className="mr-1" /> Agregar
                </Button>
              </div>
              <div className="flex gap-1 flex-wrap">
                {schedForm.time_slots.length === 0 ? (
                  <span className="text-xs text-gray-500">Sin horarios</span>
                ) : (
                  schedForm.time_slots.map((s) => (
                    <Badge key={s} variant="outline" className="bg-[#0A0A0A] text-white border-white/20 flex items-center gap-1 py-1.5 px-2 font-['JetBrains_Mono']" data-testid={`sched-slot-${s}`}>
                      {s}
                      <button type="button" onClick={() => removeSlot(s)} className="text-gray-500 hover:text-red-400 ml-1">
                        <XIcon size={10} />
                      </button>
                    </Badge>
                  ))
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-3 border-t border-white/10">
              <Button size="sm" variant="ghost" onClick={() => setEditingSched(false)} className="text-gray-400 text-xs uppercase font-bold">Cancelar</Button>
              <Button size="sm" onClick={saveSched} disabled={savingSched} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="sched-save-btn">
                {savingSched ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold flex items-center gap-1"><CurrencyDollar size={12} /> Precios</p>
              <div className="space-y-1 font-['JetBrains_Mono']">
                <p className="text-white">1h: <span className="text-[#CCFF00]">${Number(schedule.prices["1h"] || 0).toFixed(2)}</span></p>
                <p className="text-white">1.5h: <span className="text-[#CCFF00]">${Number(schedule.prices["1.5h"] || 0).toFixed(2)}</span></p>
                <p className="text-white">2h: <span className="text-[#CCFF00]">${Number(schedule.prices["2h"] || 0).toFixed(2)}</span></p>
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold flex items-center gap-1"><CalendarCheck size={12} /> Dias</p>
              <div className="flex gap-1 flex-wrap">
                {DAY_NAMES.map((n, i) => (
                  <span key={i} className={`px-2 py-0.5 text-[10px] font-semibold uppercase border ${schedule.operating_days.includes(i) ? "bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30" : "bg-white/5 text-gray-600 border-white/10"}`}>
                    {n}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold flex items-center gap-1"><Clock size={12} /> Horarios ({schedule.time_slots.length})</p>
              <div className="flex gap-1 flex-wrap">
                {schedule.time_slots.slice(0, 14).map((s) => (
                  <span key={s} className="px-2 py-0.5 text-[10px] bg-white/5 text-gray-300 border border-white/10 font-['JetBrains_Mono']">{s}</span>
                ))}
                {schedule.time_slots.length > 14 && <span className="text-xs text-gray-500">+{schedule.time_slots.length - 14}</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Date selector */}
      <div className="card-tactical p-4 flex items-center gap-3 flex-wrap">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">Ver disponibilidad para</span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-[#0A0A0A] border border-white/20 text-white h-10 px-3 focus:border-[#CCFF00] text-sm"
          data-testid="club-detail-date"
        />
        <span className="text-xs text-gray-500 font-['JetBrains_Mono']">{rentals.filter(r => r.status !== "cancelada").length} reservas</span>
      </div>

      {/* Floor plan */}
      <div className="card-tactical p-6" data-testid="court-grid">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Plano</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white">Canchas ({courts.length})</h2>
          </div>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider">
            <span className="flex items-center gap-1 text-green-400"><span className="w-2 h-2 bg-green-400" />Libre</span>
            <span className="flex items-center gap-1 text-orange-400"><span className="w-2 h-2 bg-orange-400" />Ocupada</span>
            <span className="flex items-center gap-1 text-[#CCFF00]"><span className="w-2 h-2 bg-[#CCFF00]" />Confirmada</span>
          </div>
        </div>

        {courts.length === 0 ? (
          <p className="text-center text-gray-500 py-12">Este club aun no tiene canchas registradas.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {courts.map((courtName, idx) => {
              const bookings = courtBookings(courtName);
              const isBusy = bookings.length > 0;
              const hasConfirmed = bookings.some((b) => b.status === "confirmada");
              const borderColor = hasConfirmed
                ? "border-[#CCFF00]/60"
                : isBusy
                ? "border-orange-500/40"
                : "border-green-500/30";
              const slots = schedule.time_slots.length > 0 ? schedule.time_slots : [];
              return (
                <div
                  key={idx}
                  className={`bg-[#0A0A0A] border ${borderColor} p-4 flex flex-col gap-3 transition-all hover:border-[#CCFF00]/60`}
                  data-testid={`court-tile-${idx}`}
                >
                  {/* Live scoreboard for this court (shown above the court visual) */}
                  <CourtScoreboardWidget clubId={clubId} courtName={courtName} />

                  <PadelCourt3D name={courtName} size={300} className="w-full" testId={`court-3d-${idx}`} />

                  {/* Per-court streaming controls */}
                  <ClubStreamingSetup clubId={clubId} courtName={courtName} isAdmin={canManage} compact={true} />

                  {/* Training panel (clinic/class logging) */}
                  <TrainingPanel
                    clubId={clubId}
                    courtName={courtName}
                    canManage={canManage || ["profesor"].includes(user?.role)}
                    testId={`training-panel-${idx}`}
                  />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TennisBall size={18} weight="duotone" className="text-[#CCFF00]" />
                      <span className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">{courtName}</span>
                    </div>
                    <Badge variant="outline" className={`badge-status text-[10px] ${
                      hasConfirmed ? "bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/40"
                      : isBusy ? "bg-orange-500/20 text-orange-400 border-orange-500/30"
                      : "bg-green-500/20 text-green-400 border-green-500/30"
                    }`}>
                      {bookings.length}/{slots.length || "?"}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-7 gap-1">
                    {slots.map((slot) => {
                      const b = bookings.find((x) => x.time_slot === slot);
                      const color = !b
                        ? "bg-white/5 border-white/10"
                        : b.status === "confirmada"
                        ? "bg-[#CCFF00]/30 border-[#CCFF00]/50"
                        : "bg-orange-500/30 border-orange-500/40";
                      return (
                        <div key={slot} className={`h-5 border ${color}`} title={`${slot}${b ? ` - ${b.user_name}` : " libre"}`} />
                      );
                    })}
                  </div>

                  <Button
                    size="sm"
                    onClick={() => reserveCourt(courtName)}
                    className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs w-full"
                    data-testid={`reserve-court-${idx}`}
                  >
                    <CalendarCheck size={12} className="mr-1" /> Reservar
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ClubFeed clubId={clubId} clubName={club.name} instagramHandle={club.instagram_handle} />

      {/* Aggregated court history (matches/retas/rentals/tournaments) */}
      <CourtHistory clubId={clubId} courtNames={courts} />

      {/* Theme editor (admin or directivo of this club) */}
      {canManage && <ClubPaymentMethodsBlock clubId={clubId} clubName={club.name} />}
      {canManage && <ClubThemeEditor clubId={clubId} clubName={club.name} />}
    </div>
  );
}

function ClubPaymentMethodsBlock({ clubId, clubName }) {
  const [methods, setMethods] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/clubs/${clubId}/payment-methods`).then(({ data }) => setMethods(data.payment_methods || [])).catch(() => {});
  }, [open, clubId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clubs/${clubId}/payment-methods`, { payment_methods: methods });
      toast.success(`Métodos de ${clubName} guardados`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-tactical p-6" data-testid="club-payment-methods-block">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">Cobros del Club</p>
          <h3 className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Métodos de pago alternativos</h3>
          <p className="text-xs text-gray-400 mt-1">Configurar links, transferencias, PayPal o instrucciones manuales para suscripciones de jugadores asociadas a este club.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(o => !o)} className="border-white/10 text-xs uppercase" data-testid="club-toggle-payment-methods">
          {open ? "Ocultar" : "Configurar"}
        </Button>
      </div>
      {open && (
        <>
          <ClubMethodsEditor value={methods} onChange={setMethods} />
          <div className="flex justify-end mt-4">
            <Button onClick={save} disabled={busy} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="club-methods-save">{busy ? "Guardando..." : "Guardar métodos"}</Button>
          </div>
        </>
      )}
    </div>
  );
}

function ClubMethodsEditor(props) {
  const [Editor, setEditor] = useState(null);
  useEffect(() => {
    import("@/pages/PricingPlans").then(mod => setEditor(() => mod.PaymentMethodsEditor));
  }, []);
  if (!Editor) return <p className="text-xs text-gray-500">Cargando editor...</p>;
  return <Editor {...props} />;
}

function ClubThemeEditor({ clubId, clubName }) {
  const [theme, setTheme] = useState(null);
  const [hasOverride, setHasOverride] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/theme/club/${clubId}`);
      setTheme(r.data.theme);
      setHasOverride(r.data.has_override);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open, clubId]);

  const update = (key, value) => {
    const next = { ...theme, [key]: value };
    setTheme(next);
    // live preview via CSS vars
    document.documentElement.style.setProperty(`--theme-${key.replace("_", "-")}`, value);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/theme/club/${clubId}`, theme);
      toast.success(`Tema de ${clubName} guardado`);
      setHasOverride(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Quitar el tema personalizado? Volverá al tema global del ecosistema.")) return;
    try {
      await api.delete(`/theme/club/${clubId}`);
      toast.success("Tema removido");
      setHasOverride(false);
      window.location.reload();
    } catch (e) { toast.error("Error"); }
  };

  const FIELDS = [
    { key: "primary", label: "Primario" },
    { key: "primary_dark", label: "Primario hover" },
    { key: "bg", label: "Fondo" },
    { key: "surface", label: "Superficie" },
    { key: "text", label: "Texto" },
    { key: "danger", label: "Peligro" },
    { key: "success", label: "Éxito" },
  ];

  return (
    <div className="bg-white/5 border border-white/10 p-4 space-y-3" data-testid="club-theme-editor">
      <button onClick={() => setOpen((o) => !o)} className="text-xs uppercase tracking-wider text-gray-300 hover:text-[#CCFF00] flex items-center gap-2 font-bold" data-testid="btn-toggle-club-theme">
        🎨 {open ? "Ocultar" : "Personalizar"} colores del club
        {hasOverride && <Badge className="bg-amber-500/20 text-amber-200 text-[10px]">Activo</Badge>}
      </button>
      {open && theme && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">
            Estos colores aplican <b>solo cuando los visitantes vean este club</b> (vista detalle, AR, link público).
            Si no defines tema, hereda el del ecosistema.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center gap-2 p-2 bg-black/30 border border-white/10">
                <input type="color" value={theme[f.key]?.startsWith("#") ? theme[f.key] : "#000000"}
                  onChange={(e) => update(f.key, e.target.value)}
                  className="w-10 h-10 border-0 cursor-pointer"
                  data-testid={`club-color-${f.key}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">{f.label}</div>
                  <div className="text-[10px] font-mono text-gray-300 truncate">{theme[f.key]}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={save} disabled={busy} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="btn-save-club-theme">
              Guardar tema del club
            </Button>
            {hasOverride && (
              <Button onClick={remove} variant="outline" className="text-red-400 border-red-400/40" data-testid="btn-clear-club-theme">
                Quitar tema personalizado
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
